import { Badge, type DataColumn, DataTable, EnumSelect, useDataGrid } from "@nymbl/nos2-rules";
import { ScrollText } from "lucide-react";
import { SettingsPage } from "@/components/layout/SettingsPage";
import { type AuditLogEntry, useAuditLog } from "@/hooks/use-audit";
// `diffChanges` lives in the dependency-free `@/lib/diff` (not `@/lib/audit`) so
// this client component doesn't pull the server-only audit module's
// drizzle/postgres deps into the browser bundle.
import { diffChanges } from "@/lib/diff";
import { AUDIT_ACTION_OPTIONS } from "@/lib/enums";
import { formatDateTime } from "@/lib/format";

function summarizeChange(entry: AuditLogEntry): string {
  if (!entry.before && entry.after) return "created";
  if (entry.before && !entry.after) return "deleted";
  if (entry.before && entry.after) {
    const keys = diffChanges(entry.before, entry.after);
    return keys.length > 0 ? keys.join(", ") : "—";
  }
  const email = entry.metadata?.email;
  if (typeof email === "string") return email;
  return "—";
}

const COLUMNS: DataColumn<AuditLogEntry>[] = [
  {
    key: "createdAt",
    label: "When",
    type: "date",
    filterable: false,
    render: (entry) => (
      <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
    ),
  },
  {
    key: "actor",
    label: "Actor",
    type: "text",
    sortable: false,
    filterable: false,
    render: (entry) => (
      <span className="block max-w-[200px] truncate font-medium">
        {entry.actorEmail ?? entry.actorId ?? "system"}
      </span>
    ),
    exportValue: (entry) => entry.actorEmail ?? entry.actorId ?? "system",
  },
  {
    key: "action",
    label: "Action",
    type: "text",
    filterable: false,
    render: (entry) => (
      <Badge variant="outline" className="font-mono text-xs">
        {entry.action}
      </Badge>
    ),
  },
  {
    key: "entityType",
    label: "Entity",
    type: "text",
    filterable: false,
    render: (entry) => (
      <span className="text-muted-foreground">
        {entry.entityType}
        {entry.entityId ? <span> · {entry.entityId.slice(0, 8)}</span> : null}
      </span>
    ),
    exportValue: (entry) =>
      entry.entityId ? `${entry.entityType} ${entry.entityId}` : entry.entityType,
  },
  {
    key: "changes",
    label: "Changes",
    type: "text",
    sortable: false,
    filterable: false,
    render: (entry) => (
      <span className="block max-w-xs truncate text-muted-foreground">
        {summarizeChange(entry)}
      </span>
    ),
    exportValue: summarizeChange,
  },
];

export function AuditLogTab() {
  const grid = useDataGrid({ defaultSort: "createdAt", storageKey: "dashboard:audit-log:v1" });
  return (
    <SettingsPage>
      <DataTable<AuditLogEntry>
        useRows={useAuditLog}
        grid={grid}
        rowKey={(entry) => entry.id}
        label="Audit log"
        columns={COLUMNS}
        defaultSort="createdAt"
        defaultSortDir="desc"
        search={{ placeholder: "Search by actor or entity…" }}
        itemLabel="entries"
        statusLabel={(action) =>
          `Action: ${AUDIT_ACTION_OPTIONS.find((o) => o.value === action)?.label ?? action}`
        }
        empty={{ title: "No audit entries", icon: ScrollText }}
        controls={
          <EnumSelect
            value={grid.status}
            onValueChange={grid.setStatus}
            options={AUDIT_ACTION_OPTIONS}
            allOption={{ label: "All actions" }}
            placeholder="All actions"
            className="w-[220px]"
            aria-label="Filter by action"
          />
        }
      />
    </SettingsPage>
  );
}
